import { createEntityAdapter, EntityState } from "@ngrx/entity";
import { createReducer, on } from "@ngrx/store";
import {
  ImaxxActions,
  TreatmentPageActions,
  TreatmentPlanApiActions,
} from "../actions/action-index";
import { mapOptimizedTreatmentPlan, mapTreatmentPlan } from "./maps";
import { TreatmentPlan } from "./models/treatment-plan";

export const featureKey = "treatmentPlans";

export interface TreatmentPlanState extends EntityState<TreatmentPlan> {
  tabIndex: number;
}

export const adapter = createEntityAdapter<TreatmentPlan>({
  selectId: (treatmentPlan: TreatmentPlan) => treatmentPlan.id,
  sortComparer: (a, b) => (a.sortOrder > b.sortOrder ? 1 : -1),
});

export const initialState: TreatmentPlanState = adapter.getInitialState({
  tabIndex: null,
});

export const reducer = createReducer(
  initialState,
  on(TreatmentPlanApiActions.loadSuccess, (state, { contract }) =>
    adapter.setAll(
      contract.TreatmentPlans.map((tp, idx) => mapTreatmentPlan(tp, idx, 0)),
      {
        ...state,
        tabIndex: 0,
      }
    )
  ),
  on(TreatmentPageActions.treatmentPlanSelected, (state, { index }) => {
    return {
      ...state,
      tabIndex: index,
    };
  }),
  on(TreatmentPageActions.treatmentPlanRemoved, (state, { id }) => ({
    ...adapter.removeOne(id, state),
    tabIndex: Math.min(state.tabIndex, Math.max(0, state.ids.length - 2)),
  })),
  on(
    TreatmentPageActions.treatmentPlanTemplateAdded,
    (state, { treatmentPlan }) => ({
      ...adapter.addOne(
        { ...treatmentPlan, sortOrder: state.ids.length },
        state
      ),
      tabIndex: state.ids.length,
    })
  ),
  on(
    TreatmentPageActions.treatmentNameChanged,
    (state, { treatmentPlanId, name }) =>
      adapter.updateOne(
        {
          id: treatmentPlanId,
          changes: { name },
        },
        state
      )
  ),
  on(
    TreatmentPageActions.acceledentChanged,
    (state, { treatmentPlanId, acceledent }) =>
      adapter.updateOne(
        {
          id: treatmentPlanId,
          changes: { offerAcceledent: acceledent },
        },
        state
      )
  ),
  on(
    TreatmentPageActions.treatmentLengthChanged,
    (state, { treatmentPlanId, treatmentLength }) =>
      adapter.updateOne(
        {
          id: treatmentPlanId,
          changes: {
            maxDuration: treatmentLength.maxDuration,
            minDuration: treatmentLength.minDuration,
          },
        },
        state
      )
  ),
  on(
    TreatmentPlanApiActions.createTreatmentPlanSuccess,
    (state, { originalId, treatmentPlan }) => {
      if (treatmentPlan) {
        if (originalId) {
          const sortOrder = state.entities[originalId].sortOrder;
          return adapter.addOne(
            mapTreatmentPlan(treatmentPlan, sortOrder, 0),
            adapter.removeOne(originalId, { ...state, tabIndex: sortOrder })
          );
        }
        return adapter.addOne(
          mapTreatmentPlan(treatmentPlan, state.ids.length, 0),
          {
            ...state,
            tabIndex: state.ids.length,
          }
        );
      } else {
        return state;
      }
    }
  ),
  on(
    TreatmentPlanApiActions.createTreatmentPlanOptimizerSuccess,
    (state, { originalId, imaxxOptimized }) => {
      if (imaxxOptimized) {
        if (originalId) {
          const sortOrder = state.entities[originalId].sortOrder;
          return adapter.addOne(
            mapOptimizedTreatmentPlan(imaxxOptimized, sortOrder, 1),
            adapter.removeOne(originalId, { ...state, tabIndex: sortOrder })
          );
        }
        return adapter.addOne(
          mapOptimizedTreatmentPlan(imaxxOptimized, state.ids.length, 1),
          {
            ...state,
            tabIndex: state.ids.length,
          }
        );
      } else {
        return state;
      }
    }
  ),
  on(ImaxxActions.updateImaxxOptimizerSuccess, (state, { TreatmentPlanId }) =>
    adapter.updateOne(
      {
        id: TreatmentPlanId,
        changes: {
          isOptimizable: true,
        },
      },
      state
    )
  )
);
